import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, type TooltipProps } from '~/components/Tooltip';
import { cn } from '~/lib/cn';
import { PerformanceDeltaLabel } from '~/modules/artist-roster/components/TrackList/PerformanceDeltaLabel';

import { HORIZONTAL_GRID_CLASS } from './HorizontalAxis';
import { HorizontalBar } from './HorizontalBar';
import type { BarChartDirection } from './types';
import { VerticalBar } from './VerticalBar';

interface BarChartBarProps extends HTMLAttributes<HTMLDivElement> {
  percentageValues: number[];
  categoryIcon?: ReactNode;
  categoryLabel: string;
  valueLabel: string;
  direction: BarChartDirection;
  delta?: number;
  barActive?: RefObject<HTMLDivElement>;
  setBarActive: (element?: RefObject<HTMLDivElement>) => void;
  axisLabels: number[];
  tooltip?: TooltipProps;
}

export const BarChartBar = ({
  percentageValues,
  categoryIcon,
  categoryLabel,
  valueLabel,
  direction,
  delta,
  barActive,
  setBarActive,
  axisLabels,
  tooltip,
  ...props
}: BarChartBarProps) => {
  const { t } = useTranslation('roster');
  const isVertical = direction === 'vertical';
  const isHorizontal = direction === 'horizontal';
  const AxisBar = isVertical ? VerticalBar : HorizontalBar;
  const axisBar = (
    <AxisBar
      {...{
        percentageValues,
        barActive,
        setBarActive,
        axisLabels,
      }}
    />
  );

  return (
    <div
      {...props}
      className={cn('grid grow gap-sm', {
        'max-w-xs grid-rows-8': isVertical,
        [HORIZONTAL_GRID_CLASS]: isHorizontal,
      })}
    >
      <div
        className={cn('flex items-center text-body-sm font-bold', {
          'col-span-1': isHorizontal,
          'order-2 row-span-1 flex-col justify-end': isVertical,
        })}
      >
        {categoryIcon ? (
          <div className="flex flex-col items-center gap-sm">
            {categoryIcon}
            {categoryLabel}
          </div>
        ) : (
          categoryLabel
        )}
      </div>
      <div
        className={cn('grid', {
          'order-1 row-span-6 items-stretch justify-center px-sm py-px':
            isVertical,
          'col-span-6 items-center py-sm': isHorizontal,
        })}
      >
        {tooltip ? <Tooltip {...tooltip}>{axisBar}</Tooltip> : axisBar}
      </div>
      <div
        className={cn('col-span-1 grid content-center gap-xs', {
          'justify-end text-right': isHorizontal,
          'order-3 text-center': isVertical,
        })}
      >
        <div className="text-body-sm">{valueLabel}</div>
        {delta ? (
          <PerformanceDeltaLabel
            delta={delta}
            className={cn({
              'justify-end': isHorizontal,
              'justify-center': isVertical,
            })}
            dateRange={t('trackDetails.labels.wow')}
          />
        ) : (
          <div className="select-none text-body-xs">&nbsp;</div>
        )}
      </div>
    </div>
  );
};

BarChartBar.displayName = 'BarChartBar';
